import { apiErrorMessage, oneSpec, type Prefetch } from '@bemor/web-kit';
import {
    AsOfBadge,
    Card,
    CardContent,
    DateTime,
    DetailRow,
    type ListColumnDef,
    PageHeader,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { NotificationLogRow, NotificationStats } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useOne } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/notifications/log';
const PAGE_KEY = 'notification-logs';

const logFilters = filterDefs(PATH, {
    q: 'Search',
    template_key: 'Template',
    channel: 'Channel',
    status: 'Status',
    notifiable_type: 'Recipient type',
    merchant_id: 'Merchant',
    range: 'Sent',
});

/** The extra reads of the page: Nexroll's statistics (for the list's dates) and the row in the drawer. */
export const notificationLogsReads = (
    id?: string,
    dates: { from?: string; to?: string } = {},
) => ({
    stats: oneSpec(PATH + '/stats', {
        ...(dates.from ? { from_date: dates.from } : {}),
        ...(dates.to ? { to_date: dates.to } : {}),
    }),
    detail: id ? oneSpec(`${PATH}/${id}`) : null,
});

const listPrefetch = adminListPrefetch(PAGE_KEY, PATH);

export const notificationLogsPrefetch: Prefetch = (context) => {
    const r = notificationLogsReads(context.params.id);
    return [...listPrefetch(context), r.stats, ...(r.detail ? [r.detail] : [])];
};

const CHANNELS: Record<string, string> = {
    email: 'Email',
    in_app: 'In-app',
    web_push: 'Push',
};

function Counts({
    title,
    counts,
    labels,
}: {
    title: string;
    counts: Record<string, number> | undefined;
    labels?: Record<string, string>;
}) {
    const entries = Object.entries(counts ?? {});
    return (
        <Card>
            <CardContent className="p-4">
                <p className="text-muted-foreground text-xs font-medium">
                    {title}
                </p>
                {entries.length === 0 ? (
                    <p className="text-muted-foreground mt-1 text-sm">
                        No data
                    </p>
                ) : (
                    <ul className="mt-1 space-y-0.5 text-sm">
                        {entries.map(([k, n]) => (
                            <li key={k} className="flex justify-between">
                                <span className="text-muted-foreground">
                                    {labels?.[k] ?? k}
                                </span>
                                <span className="font-medium">{n}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}

/** Nexroll's notification statistics section: the count, by channel, by status and by category, within the list's dates. */
function StatsSection({ stats }: { stats: NotificationStats | undefined }) {
    return (
        <div
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="notification-stats"
        >
            <Card>
                <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs font-medium">
                        Total sent
                    </p>
                    <p className="text-2xl font-bold">{stats?.total ?? 0}</p>
                </CardContent>
            </Card>
            <Counts
                title="By channel"
                counts={stats?.by_channel}
                labels={CHANNELS}
            />
            <Counts title="By status" counts={stats?.by_status} />
            <Counts title="By category" counts={stats?.by_category} />
        </div>
    );
}

function Drawer({
    row,
    loading,
    error,
    open,
    onOpenChange,
}: {
    row: NotificationLogRow | null;
    loading: boolean;
    error: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full overflow-y-auto p-0 sm:max-w-2xl"
                data-testid="notification-log-drawer"
            >
                <SheetTitle className="sr-only">Notification</SheetTitle>
                <SheetDescription className="sr-only">
                    The full notification content and metadata
                </SheetDescription>
                <div className="flex flex-col gap-4 p-5">
                    <h2 className="text-lg font-semibold">
                        Notification details
                    </h2>
                    {error ? (
                        <p role="alert" className="text-destructive text-sm">
                            {error}
                        </p>
                    ) : null}
                    {row ? (
                        <>
                            <DetailRow
                                label="Template"
                                value={
                                    <span className="font-mono text-xs">
                                        {row.template_key}
                                    </span>
                                }
                            />
                            <DetailRow
                                label="Channel"
                                value={CHANNELS[row.channel] ?? row.channel}
                            />
                            <DetailRow
                                label="Status"
                                value={
                                    <StatusBadge
                                        domain={ADMIN_STATUSES.notification}
                                        status={row.status}
                                    />
                                }
                            />
                            <DetailRow
                                label="Sent at"
                                value={
                                    <DateTime
                                        zone="inline"
                                        value={row.created_at}
                                    />
                                }
                            />
                            <DetailRow
                                label="Recipient"
                                value={`${row.notifiable_type} ${row.notifiable_id}`}
                            />
                            <DetailRow
                                label="Merchant"
                                value={row.merchant_id ?? '—'}
                            />
                            <DetailRow label="Title" value={row.title} />
                            <DetailRow
                                label="Body"
                                value={
                                    <span className="whitespace-pre-wrap">
                                        {row.body}
                                    </span>
                                }
                            />
                            {row.cta ? (
                                <DetailRow
                                    label="Call to action"
                                    value={`${row.cta.label} ${row.cta.url ?? ''}`}
                                />
                            ) : null}
                            {row.variables &&
                            Object.keys(row.variables).length > 0 ? (
                                <DetailRow
                                    label="Variables (sanitized)"
                                    value={
                                        <pre className="bg-muted overflow-auto rounded p-2 text-xs">
                                            {JSON.stringify(
                                                row.variables,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    }
                                />
                            ) : null}
                            {row.error_message ? (
                                <DetailRow
                                    label="Error"
                                    value={
                                        <span className="text-destructive">
                                            {row.error_message}
                                        </span>
                                    }
                                />
                            ) : null}
                            <DetailRow
                                label="Source event"
                                value={
                                    <span className="font-mono text-xs">
                                        {row.source_event_id}
                                    </span>
                                }
                            />
                        </>
                    ) : loading ? (
                        <p className="text-muted-foreground text-sm">
                            Loading…
                        </p>
                    ) : null}
                </div>
            </SheetContent>
        </Sheet>
    );
}

/**
 * `/notification-logs` and `/notification-logs/:id` (BM-103): Nexroll's Notification Logs page. Every notification
 * Delivery produced for this tenant, newest first, with the statistics on top and the full row in a drawer.
 */
export function NotificationLogsPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [params] = useSearchParams();
    const columns: ListColumnDef<NotificationLogRow>[] = [
        {
            key: 'template_key',
            label: 'Template',
            sortKey: 'template_key',
            locked: 'start',
            cell: (n) => (
                <span className="font-mono text-xs">{n.template_key}</span>
            ),
        },
        {
            key: 'recipient',
            label: 'Recipient',
            sortKey: 'notifiable_id',
            cell: (n) => (
                <span>
                    <span className="text-muted-foreground">
                        {n.notifiable_type}
                    </span>{' '}
                    <span className="font-mono text-xs">{n.notifiable_id}</span>
                </span>
            ),
        },
        {
            key: 'title',
            label: 'Title',
            cell: (n) => n.title,
        },
        {
            key: 'channel',
            label: 'Channel',
            sortKey: 'channel',
            cell: (n) => CHANNELS[n.channel] ?? n.channel,
        },
        {
            key: 'status',
            label: 'Status',
            sortKey: 'status',
            cell: (n) => (
                <StatusBadge
                    domain={ADMIN_STATUSES.notification}
                    status={n.status}
                />
            ),
        },
        {
            key: 'created_at',
            label: 'Sent at',
            kind: 'datetime',
            sortKey: 'created_at',
            cell: (n) => <DateTime value={n.created_at} empty="—" />,
        },
    ];
    const { state, query } = useAdminList<NotificationLogRow>(PAGE_KEY, PATH, {
        columns,
        filters: logFilters,
    });
    const reads = notificationLogsReads(id, {
        from: params.get('from') ?? undefined,
        to: params.get('to') ?? undefined,
    });
    const stats = useOne<NotificationStats>(
        reads.stats.path,
        reads.stats.params,
    );
    const detail = useOne<NotificationLogRow>(reads.detail?.path ?? null);
    const search = params.toString() ? `?${params.toString()}` : '';
    return (
        <div className="space-y-4">
            <PageHeader
                title="Notification logs"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Every notification Delivery produced for this
                            tenant, newest first.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <StatsSection stats={stats.data?.data} />
            <AdminListPage
                noun="notifications"
                state={state}
                query={query}
                rowKey={(n) => n.id}
                exportable={false}
                onRow={(n) => navigate(`/notification-logs/${n.id}${search}`)}
                controls={{
                    q: { kind: 'search' },
                    channel: { options: Object.keys(CHANNELS) },
                    status: { domain: ADMIN_STATUSES.notification },
                    template_key: { kind: 'text', label: 'Template key' },
                    notifiable_type: {
                        options: ['staff', 'merchant_user', 'buyer'],
                    },
                    merchant_id: { kind: 'text', label: 'Merchant id' },
                    range: { label: 'Sent' },
                }}
            />
            <Drawer
                open={Boolean(id)}
                onOpenChange={(o) => {
                    if (!o) navigate(`/notification-logs${search}`);
                }}
                row={detail.data?.data ?? null}
                loading={detail.isPending && Boolean(id)}
                error={detail.isError ? apiErrorMessage(detail.error) : null}
            />
        </div>
    );
}
